import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Brain, 
  Heart, 
  TrendingUp, 
  Users, 
  Sparkles, 
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import AnalyticsApiService, { ConversationInsights } from '../../services/analyticsApi';
import { QuickMetricsCard } from './QuickMetricsCard';
import { EmotionAnalysisCard } from './EmotionAnalysisCard';
import { DeepInsightsCard } from './DeepInsightsCard';
import { IntroductionQualityCard } from './IntroductionQualityCard';
import { AIFriendEffectivenessCard } from './AIFriendEffectivenessCard';
import { NetworkValueCard } from './NetworkValueCard';

interface ConversationAnalysisPanelProps {
  conversationId: number;
  conversationTitle?: string;
  className?: string;
  hideHeader?: boolean;
  onRefresh?: () => void;
}

type AnalysisDepth = 'quick' | 'standard' | 'deep';

export const ConversationAnalysisPanel: React.FC<ConversationAnalysisPanelProps> = ({
  conversationId,
  conversationTitle,
  className = '',
  hideHeader = false,
  onRefresh
}) => {
  const [insights, setInsights] = useState<ConversationInsights | null>(null);
  const [loading, setLoading] = useState<{
    quick: boolean;
    standard: boolean;
    deep: boolean;
  }>({
    quick: true,
    standard: false,
    deep: false
  });
  const [error, setError] = useState<string | null>(null);
  const [selectedDepth, setSelectedDepth] = useState<AnalysisDepth>('quick');
  const [refreshing, setRefreshing] = useState(false);

  // Load quick metrics immediately
  useEffect(() => {
    loadInsights('quick');
  }, [conversationId]);

  const loadInsights = async (depth: AnalysisDepth, forceRefresh: boolean = false) => {
    try {
      setLoading(prev => ({ ...prev, [depth]: true }));
      setError(null);

      const data = await AnalyticsApiService.getConversationInsights(
        conversationId,
        { depth, force_refresh: forceRefresh }
      );

      setInsights(data);
      setSelectedDepth(depth);
    } catch (err) {
      console.error(`Error loading ${depth} insights:`, err);
      setError(`Failed to load ${depth} analysis. Please try again.`);
    } finally {
      setLoading(prev => ({ ...prev, [depth]: false }));
      setRefreshing(false);
    }
  };

  const handleTabChange = (value: string) => {
    const depth = value as AnalysisDepth;
    setSelectedDepth(depth);

    // Load data if not already loaded
    if (depth === 'standard' && !insights?.emotion_analysis && !loading.standard) {
      loadInsights('standard');
    } else if (depth === 'deep' && !insights?.deep_insights && !loading.deep) {
      loadInsights('deep');
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    if (onRefresh) {
      onRefresh();
    }
    loadInsights(selectedDepth, true);
  };

  if (error && !insights) {
    return (
      <Card className={`border-red-200 ${className}`}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            <p>{error}</p>
            <Button 
              onClick={() => loadInsights('quick')} 
              variant="outline" 
              size="sm"
            >
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      {!hideHeader && (
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-purple-600" />
                Conversation Analysis
              </CardTitle>
              {conversationTitle && (
                <CardDescription>{conversationTitle}</CardDescription>
              )}
            </div>
            <Button
              onClick={handleRefresh}
              variant="outline"
              size="sm"
              disabled={refreshing || loading[selectedDepth]}
            >
              {refreshing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh
                </>
              )}
            </Button>
          </div>
        </CardHeader>
      )}
      <CardContent className={hideHeader ? 'pt-6' : ''}>
        <Tabs value={selectedDepth} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="quick" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Quick
              {insights?.quick_metrics?.cached && (
                <Badge variant="secondary" className="ml-1 text-xs">Cached</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="standard" className="flex items-center gap-2">
              <Heart className="h-4 w-4" />
              Emotion & Sentiment
              {loading.standard && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
            </TabsTrigger>
            <TabsTrigger value="deep" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Deep Analysis
              {loading.deep && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
            </TabsTrigger>
          </TabsList>

          {/* Quick Metrics Tab */}
          <TabsContent value="quick" className="space-y-4 mt-4">
            {loading.quick && !insights ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              </div>
            ) : insights?.quick_metrics ? (
              <QuickMetricsCard metrics={insights.quick_metrics} />
            ) : (
              <div className="text-center py-12 text-gray-500">
                No quick metrics available
              </div>
            )}
          </TabsContent>

          {/* Standard Analysis Tab */}
          <TabsContent value="standard" className="space-y-4 mt-4">
            {loading.standard && !insights?.emotion_analysis ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                <p className="text-sm text-gray-600">
                  Running emotion and sentiment analysis...
                </p>
                <p className="text-xs text-gray-400">
                  This may take a few seconds
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {insights?.emotion_analysis && (
                  <EmotionAnalysisCard analysis={insights.emotion_analysis} />
                )}
                {insights?.sentiment_analysis && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-blue-600" />
                        Sentiment Analysis
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Overall Sentiment Banner */}
                        <div className={`p-4 rounded-lg border-2 ${
                          insights.sentiment_analysis.sentiment_polarity === 'positive' 
                            ? 'bg-green-50 border-green-200' 
                            : insights.sentiment_analysis.sentiment_polarity === 'negative'
                            ? 'bg-red-50 border-red-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-gray-600 mb-1">Overall Sentiment</p>
                              <p className={`text-2xl font-bold capitalize ${
                                insights.sentiment_analysis.sentiment_polarity === 'positive' 
                                  ? 'text-green-700' 
                                  : insights.sentiment_analysis.sentiment_polarity === 'negative'
                                  ? 'text-red-700'
                                  : 'text-gray-700'
                              }`}>
                                {insights.sentiment_analysis.sentiment_polarity === 'positive' ? '👍' : 
                                 insights.sentiment_analysis.sentiment_polarity === 'negative' ? '👎' : '😐'}
                                {' '}
                                {insights.sentiment_analysis.sentiment_polarity}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600 mb-1">Score</p>
                              <p className="text-2xl font-bold text-gray-900">
                                {insights.sentiment_analysis.sentiment_score.toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Sentiment Distribution */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium text-gray-700">Sentiment Distribution</h4>
                          
                          {/* Positive */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <span className="text-green-600">👍</span>
                                <span className="text-green-700 font-medium">Positive Messages</span>
                              </span>
                              <span className="font-mono text-green-700 font-semibold">
                                {(insights.sentiment_analysis.positive_ratio * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="absolute h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all"
                                style={{ width: `${insights.sentiment_analysis.positive_ratio * 100}%` }}
                              />
                            </div>
                          </div>

                          {/* Negative */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <span className="text-red-600">👎</span>
                                <span className="text-red-700 font-medium">Negative Messages</span>
                              </span>
                              <span className="font-mono text-red-700 font-semibold">
                                {(insights.sentiment_analysis.negative_ratio * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="absolute h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full transition-all"
                                style={{ width: `${insights.sentiment_analysis.negative_ratio * 100}%` }}
                              />
                            </div>
                          </div>

                          {/* Neutral (calculated) */}
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="flex items-center gap-2">
                                <span className="text-gray-600">😐</span>
                                <span className="text-gray-700 font-medium">Neutral Messages</span>
                              </span>
                              <span className="font-mono text-gray-700 font-semibold">
                                {(100 - (insights.sentiment_analysis.positive_ratio * 100) - (insights.sentiment_analysis.negative_ratio * 100)).toFixed(1)}%
                              </span>
                            </div>
                            <div className="relative h-3 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className="absolute h-full bg-gradient-to-r from-gray-400 to-gray-600 rounded-full transition-all"
                                style={{ width: `${100 - (insights.sentiment_analysis.positive_ratio * 100) - (insights.sentiment_analysis.negative_ratio * 100)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {!insights?.emotion_analysis && !loading.standard && (
                  <div className="text-center py-12 text-gray-500">
                    <p>No emotion analysis available</p>
                    <Button 
                      onClick={() => loadInsights('standard', true)} 
                      variant="outline" 
                      size="sm" 
                      className="mt-4"
                    >
                      Generate Analysis
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Deep Insights Tab */}
          <TabsContent value="deep" className="space-y-4 mt-4">
            {loading.deep && !insights?.deep_insights ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                <p className="text-sm text-gray-600">
                  Running deep AI analysis with GPT-4...
                </p>
                <p className="text-xs text-gray-400">
                  This may take 5-10 seconds
                </p>
              </div>
            ) : insights?.deep_insights ? (
              <div className="space-y-4">
                {/* Series.so Specific Insights - Horizontal Layout for Modal */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <IntroductionQualityCard 
                    analysis={insights.deep_insights.introduction_analysis} 
                  />
                  <AIFriendEffectivenessCard 
                    analysis={insights.deep_insights.ai_friend_analysis} 
                  />
                  <NetworkValueCard 
                    networkValue={insights.deep_insights.network_value}
                    studentContext={insights.deep_insights.student_entrepreneur_context}
                  />
                </div>

                {/* General Deep Insights */}
                <DeepInsightsCard insights={insights.deep_insights} />
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>No deep insights available</p>
                <Button 
                  onClick={() => loadInsights('deep', true)} 
                  variant="default" 
                  size="sm" 
                  className="mt-4"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Deep Analysis
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Analysis Status Footer */}
        {insights && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-2">
                {insights.quick_metrics?.cached && (
                  <Badge variant="outline" className="text-xs">
                    <CheckCircle className="mr-1 h-3 w-3" />
                    Cached
                  </Badge>
                )}
                {insights.timestamp && (
                  <span>
                    Last analyzed: {new Date(insights.timestamp).toLocaleString()}
                  </span>
                )}
              </div>
              {error && (
                <span className="text-red-500">{error}</span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConversationAnalysisPanel;

